import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppUpdateService } from '../../../core/services/app-update.service';

/**
 * Sticky top-of-viewport banner that appears when
 * `AppUpdateService.updateAvailable$` flips true. Operator clicks
 * "Refresh Now" → window reload picks up the new bundle.
 *
 * The decision of WHEN to render the banner (which routes opt-out)
 * lives in `AppComponent.shouldShowUpdateBanner` — this component
 * just observes the service and renders. Keep this dumb-component
 * so the routing logic doesn't get smeared across two files.
 *
 * Mirrors src/web/.../update-banner/update-banner.component.ts so
 * the operator-side UX is identical between the admin shell and the
 * POS workspace.
 */
@Component({
  selector: 'app-update-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="update-banner" *ngIf="updateService.updateAvailable$ | async">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 2v10m0 0l-3-3m3 3l3-3M4 15v2a2 2 0 002 2h12a2 2 0 002-2v-2"/>
      </svg>
      <span>A new version is available.</span>
      <button (click)="refresh()">Refresh Now</button>
      <button class="dismiss" (click)="dismiss()" aria-label="Dismiss">&times;</button>
    </div>
  `,
  styles: [`
    .update-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 20px;
      background: var(--color-primary, #C41E3A);
      color: var(--color-text-inverse, #fff);
      font-size: 14px;
      font-weight: 500;
      border-bottom: 2px solid var(--color-primary-border, #8a1429);
      box-shadow: 0 2px 8px rgba(0,0,0,.10);
      animation: slide-down .3s ease;
    }
    @keyframes slide-down {
      from { transform: translateY(-100%); }
      to   { transform: translateY(0); }
    }
    svg { flex-shrink: 0; }
    span { flex: 1; }
    button {
      background: var(--color-primary-light, #fff);
      color: var(--color-primary, #C41E3A);
      border: 1px solid var(--color-primary-border, #8a1429);
      border-radius: 4px;
      padding: 5px 14px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background var(--transition-fast, .15s), color var(--transition-fast, .15s);
    }
    button:hover {
      background: var(--color-primary-border, #8a1429);
      color: var(--color-text-inverse, #fff);
    }
    .dismiss {
      background: none;
      border: none;
      color: var(--color-text-inverse, #fff);
      font-size: 20px;
      padding: 2px 6px;
      line-height: 1;
    }
  `]
})
export class UpdateBannerComponent {
  updateService = inject(AppUpdateService);

  refresh(): void {
    window.location.reload();
  }

  dismiss(): void {
    this.updateService.dismiss();
  }
}
